import { Box } from "@mui/material";

import { StyledAnchor } from "@/components/common/styles";

import { MID, marketingPalette } from "@/components/marketing/tokens";
import type { Category } from "@/content/types";

/**
 * The category pill. Its background is editorial, each category carries its own
 * colour in the CMS, so nothing here maps names to colours.
 */
// The article's pill is not the card's pill at a different size: it leads
// tighter and it is border-box over a 1px rule it never colours, which the
// cards' is not. Measured on both pages, `.b-categodry_label` vs
// `.b-categodry_innact-lbl`, they are two published elements, not one.
const ARTICLE_PILL = {
  fontSize: 14,
  px: "12px",
  py: "4px",
  lineHeight: "21px",
  border: `1px solid ${marketingPalette.surface}`,
  maxWidth: "100%",
};

const VARIANTS = {
  // The academy's `.b-categodry_label` keeps its size in a row too narrow for
  // it, and as a `.w-inline-block` never outgrows what it sits in.
  article: { ...ARTICLE_PILL, flexShrink: 0 },
  // A post's sits in a link of its own that gives way to the date beside it,
  // so in a row too narrow for both the pill wraps its words.
  post: { ...ARTICLE_PILL, flexShrink: 1 },
  card: {
    fontSize: 14,
    px: "12px",
    py: "4px",
    lineHeight: "22.4px",
    border: "none",
    // The cards' pill keeps to the top of the column it sits in; the link
    // pills above follow the row's own centring.
    alignSelf: "flex-start",
  },
  // `.b-categodry_innact-lbl`, which steps down below the card step; the
  // cards' `.is-small` outranks that rule and keeps its own.
  featured: {
    fontSize: 16,
    px: "16px",
    py: "6.08px",
    lineHeight: "22.4px",
    border: "none",
    alignSelf: "flex-start",
    [MID]: { fontSize: 14, px: "9.6px", py: "3.2px" },
  },
  // The article's taxonomy chips take the surface as their ground rather than a
  // category colour: the CMS gives a taxonomy no colour of its own.
  taxonomy: {
    fontSize: 14,
    px: "12px",
    py: "4px",
    lineHeight: "21px",
    border: `1px solid ${marketingPalette.surface}`,
    backgroundColor: marketingPalette.surface,
    flexShrink: 0,
    maxWidth: "100%",
  },
};

export default function CategoryLabel({
  category,
  variant = "card",
  linked = false,
  href,
}: {
  category: Category;
  variant?: keyof typeof VARIANTS;
  /**
   * Both article pages render the pill as a link; the cards do not, because
   * there it sits inside the card's own anchor where a second one cannot
   * legally go.
   */
  linked?: boolean;
  /**
   * Where the pill points when the category carries no `href` of its own. The
   * academy's categories bring an absolute one from the CMS; the blog's bring
   * none, so its article passes the path in. A taxonomy chip passes neither and
   * stays a plain label, which is what the published one is.
   */
  href?: string;
}) {
  const destination = category.href ?? href;
  return (
    <Box
      {...(linked && destination
        ? { component: StyledAnchor, href: destination }
        : {})}
      sx={{
        display: "flex",
        borderRadius: "1280px",
        color: marketingPalette.inkSubtle,
        ...VARIANTS[variant],
        // A taxonomy carries no colour of its own in the CMS, so the variant's
        // ground stands rather than being overwritten with an empty string.
        ...(category.color ? { backgroundColor: category.color } : {}),
      }}
    >
      {/* The published chip wraps its text in a box of its own; without it the
          pill paints one edge pair where the published one paints two. */}
      <Box component="div">{category.name}</Box>
    </Box>
  );
}
